"use client";

import React, { useEffect, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "staysure-component-library";

import RichText from "@/components/atoms/rich-text/rich-text";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { AirDoctorAssistantProps } from "@/types/confirmation-types";

function AirDoctorAssistantContent({
  airDoctorTitle,
  airDoctorSubTitle,
  bookAnAppointmentLabel,
  bookAnAppointmentLink,
  getSupportViaPhoneLabel,
  getSupportViaPhoneNumber,
  getSupportViaWhatsappLabel,
  getSupportViaWhatsappNumber,
}: Readonly<AirDoctorAssistantProps>) {
  const [emergencyAccordionOpen, setEmergencyAccordionOpen] = useState(true);
  function toggleEmergencyAccordion() {
    setEmergencyAccordionOpen(!emergencyAccordionOpen);
  }
  let phoneNumBtnStyle =
    "focus:outline-offset-4 focus:outline focus:outline-interactive-focus-default rounded-radius-pill md:mr-2 ml-2 md:ml-0";

  let textStyles =
    "text-[20px] md:text-[25px] leading-[23px] md:leading-[28px] font-bold";
  let linkTextStyles = "text-[18px] leading-[27px] font-bold";

  const isMobile = useMediaQuery("(max-width: 599px)");

  useEffect(() => {
    if (isMobile) {
      setEmergencyAccordionOpen(false);
    } else {
      setEmergencyAccordionOpen(true);
    }
  }, [isMobile]);

  const handleCall = (number: string) => {
    const numberToCall = /\d+/.exec(number.split(" ").join(""));
    const telUri = `tel:${numberToCall?.[0]}`;
    window.open(telUri, "_self");
  };

  const openWhatsApp = (number: string) => {
    if (!number) return;

    const cleanNumber = number.replaceAll(/\D/g, "");

    const whatsappUrl = `https://wa.me/${cleanNumber}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div
      className="flex flex-col gap-spacing-s md:gap-spacing-m lg:gap-spacing-l"
      data-testid="airDoctorAssistantContent-testid"
    >
      <button
        id="air-doctor-assistant-accordion-button"
        type="button"
        className="transition duration-300 w-full"
        onClick={toggleEmergencyAccordion}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            toggleEmergencyAccordion();
          }
        }}
      >
        <h1 className="flex flex-row text-left justify-between font-header-font text-text-title text-[23px] md:text-[31px] leading-[27.6px] md:leading-[34.1px] cursor-pointer">
          {airDoctorTitle}
          {emergencyAccordionOpen ? (
            <ChevronUpIcon
              stroke={selectedColorBrand["interactive-secondary-default"]}
            />
          ) : (
            <ChevronDownIcon
              stroke={selectedColorBrand["interactive-secondary-default"]}
            />
          )}
        </h1>
      </button>
      <div
        className={`space-y-spacing-s md:space-y-spacing-m lg:space-y-spacing-l overflow-hidden transition-opacity duration-500 md:ps-0
  ${emergencyAccordionOpen ? "opacity-100" : "opacity-0 hidden"}
  `}
      >
        <div>
          {airDoctorSubTitle && (
            <RichText
              richText={airDoctorSubTitle}
              className={`text-2xs font-normal leading-6 text-text-body`}
            />
          )}
        </div>

        <div
          className={`space-y-spacing-xxs md:space-y-0 md:flex md:flex-row md:justify-between`}
        >
          <p className="text-xs md:text-xs font-normal leading-6 md:leading-[27px] text-text-functional-subtle break-words">
            {bookAnAppointmentLabel}
          </p>
          {bookAnAppointmentLink && (
            <RichText
              richText={bookAnAppointmentLink}
              className={`text-[14px] break-words font-body-font leading-[18px] contact-rich-text ${linkTextStyles}`}
            />
          )}
        </div>

        <div
          className={`space-y-spacing-xxs md:space-y-0 md:flex md:flex-row md:justify-between`}
        >
          <p className="text-xs md:text-xs font-normal leading-6 md:leading-[27px] text-text-functional-subtle">
            {getSupportViaPhoneLabel}
          </p>
          <button
            id="get-support-via-phone-number"
            type="button"
            className={phoneNumBtnStyle}
            onClick={() => handleCall(getSupportViaPhoneNumber || "")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleCall(getSupportViaPhoneNumber || "");
              }
            }}
          >
            <p className={`text-text-title ${textStyles}`}>
              {getSupportViaPhoneNumber || ""}
            </p>
          </button>
        </div>

        {getSupportViaWhatsappLabel && (
          <div className="space-y-spacing-xxs md:space-y-0 md:flex md:flex-row md:justify-between">
            <p className="text-xs md:text-xs font-normal leading-6 md:leading-[27px] text-text-functional-subtle">
              {getSupportViaWhatsappLabel}
            </p>

            <button
              id="get-support-via-whatsapp-number"
              type="button"
              style={{ marginBottom: "8px" }}
              className={phoneNumBtnStyle}
              onClick={() => openWhatsApp(getSupportViaWhatsappNumber || "")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  openWhatsApp(getSupportViaWhatsappNumber || "");
              }}
            >
              <p className="text-text-title text-[20px] md:text-[25px] leading-[23px] md:leading-[28px] font-bold">
                {getSupportViaWhatsappNumber || ""}
              </p>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AirDoctorAssistantContent;
